import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import TagValueTypedValueViewer from 'notebook/tables/typed-values/components/viewers/TagValueTypedValueViewer';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ChipSize} from 'ui-library/chips/Chip';

import {SupportedFrozenSchemaFieldValue} from '../plate-record/PlateRecordConstants';
import {getTagValue} from '../plate-record/PlateRecordUtil';
import {BlobPreview} from './BlobPreview';

interface PlateRecordMetadataProps {
  schemaFieldValues?: Array<SupportedFrozenSchemaFieldValue> | null;
}

/**
 * Renders a vertical list of frozen schema field values.
 */
export function PlateRecordMetadata(props: PlateRecordMetadataProps): JSX.Element {
  const {schemaFieldValues} = props;

  return (
    <div
      {...getTestProps({component: 'PlateRecordMetadata', element: 'root'})}
      className={css(styles.metadata)}
    >
      {schemaFieldValues?.map((field, idx) => {
        const tag = getTagValue(field);
        let content = (
          <TagValueTypedValueViewer
            chipSize={ChipSize.SMALL}
            shouldPreviewImages={true}
            showChipsAsLinks={true}
            summarize={true}
            summarizeThreshold={3}
            typedValue={convertTagToTypedValue(tag)}
            textValue={tag.value || ''}
            schemaFieldRenderData={{
              name: tag.tagSchemaField.name,
              fieldType: tag.tagSchemaField.fieldType,
            }}
            shouldShowDateAndDatetimesAsChips={false}
            shouldCollapseBlobs={false}
            showSummaryChipsAsPopover={true}
          />
        );

        // We don't have previewFile info for blob links in the frozen schema tags.
        // BlobPreview fetches the needed information, which is fine because blobs are immutable.
        if (tag.tagSchemaField.fieldType === FieldType.BLOB_LINK && tag.targetBlobs != null) {
          content = <BlobPreview blobIds={tag.targetBlobs.map((b) => b.id)} />;
        }

        return (
          <div
            key={`${field.frozenMetadata.fieldName}-${idx}`}
            {...getTestProps({component: 'PlateRecordMetadata', element: 'field'})}
            className={css(styles.field)}
          >
            <div
              {...getTestProps({component: 'PlateRecordMetadata', element: 'label'})}
              className={css(styles.schemaFieldLabel)}
            >
              {field.frozenMetadata.fieldName}
            </div>
            <div {...getTestProps({component: 'PlateRecordMetadata', element: 'value'})}>{content}</div>
          </div>
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  schemaFieldLabel: {
    fontWeight: 'bold',
  },
  metadata: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    paddingBottom: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
});
